/**
 * A main Gallery component that is capable of rendering a ton of different gallery types.
 */
import React from "react";
import { connect } from "react-redux";
import { Portal } from "react-portal";

import { getActiveModule } from "../../store";
import { getIsMobileLandscape, Show } from "../../lib/utils";
import { UserEvents, UserEventsAPI } from "../../lib/userEventsAPI";
import type { GlobalState } from "../../lib/types";
import PopoverWrapper from "./Popover";
import { shouldShowGallery } from "./selectors";
import { FloorplansGallery } from "./FloorplansGallery";
import { AdvancedFloorplans } from "./AdvancedFloorplans";
import { GalleryInner } from "./GalleryInner";

/**
 * Gallery/floorplans component.
 */
class FullscreenGalleryWrapper extends React.Component {
  /**
   * Constructor.
   */
  constructor(props) {
    super(props);
    this.state = {
      isMobileLandscape: getIsMobileLandscape(),
    };
  }

  onOrientationChange = () => {
    console.log("on orientation change");
    setTimeout(() => {
      this.setState({
        isMobileLandscape: getIsMobileLandscape(),
      });
    }, 150);
  };

  /**
   * Add handler for the rotation of the device.
   * @param prevProps
   */
  componentDidMount(prevProps) {
    window.addEventListener("orientationchange", this.onOrientationChange);
  }

  componentWillUnmount() {
    window.removeEventListener("orientationchange", this.onOrientationChange);
  }

  componentDidUpdate(prevProps) {
    const { showGallery: newShowGallery, activeModule: newActiveModule, dispatch } = this.props;
    const { showGallery: prevShowGallery, activeModule: prevActiveModule } = prevProps;
    let event;

    // If gallery just became visible.
    if (!prevShowGallery && newShowGallery) {
      if (newActiveModule.isGallery()) {
        event = UserEvents.OPEN_GALLERY;
      } else if (newActiveModule.isFloorplan()) {
        event = UserEvents.OPEN_FLOORPLANS;
      } else if (newActiveModule.isAdvancedFloorplan()) {
        event = UserEvents.OPEN_FLOORPLANS;
      } else if (newActiveModule.isVideo()) {
        event = UserEvents.OPEN_YOUTUBE;
      } else if (newActiveModule.isDigitalTour()) {
        event = UserEvents.OPEN_YOUTUBE;
      }
      if (event) {
        dispatch(UserEventsAPI.send(event));
      } else {
        console.log("FAM: WTF unhandled case.");
        // console.log(pretty(newActiveModule));
      }
    } else if (prevShowGallery && !newShowGallery) {
      // Just closed.
      if (prevActiveModule.isGallery()) {
        event = UserEvents.CLOSE_GALLERY;
      } else if (prevActiveModule.isFloorplan()) {
        event = UserEvents.CLOSE_FLOORPLANS;
      } else if (prevActiveModule.isAdvancedFloorplan()) {
        event = UserEvents.CLOSE_FLOORPLANS;
      } else if (prevActiveModule.isVideo()) {
        event = UserEvents.CLOSE_YOUTUBE;
      } else if (prevActiveModule.isDigitalTour()) {
        event = UserEvents.CLOSE_YOUTUBE;
      }
      if (event) {
        dispatch(UserEventsAPI.send(event));
      } else {
        console.log("FAM: WTF unhandled case.");
        // console.log(pretty(newActiveModule));
      }
    } else if (prevShowGallery && newShowGallery && newActiveModule.role !== prevActiveModule.role) {
      // the jump between tabs or jump from the gallery to another gallery using answers.
      if (newActiveModule.isGallery()) {
        event = UserEvents.OPEN_GALLERY;
      } else if (newActiveModule.isFloorplan()) {
        event = UserEvents.OPEN_FLOORPLANS;
      } else if (newActiveModule.isAdvancedFloorplan()) {
        event = UserEvents.OPEN_FLOORPLANS;
      } else if (newActiveModule.isVideo()) {
        event = UserEvents.OPEN_YOUTUBE;
      } else if (newActiveModule.isDigitalTour()) {
        event = UserEvents.OPEN_YOUTUBE;
      }
      if (event) {
        dispatch(UserEventsAPI.send(event));
      } else {
        console.log("FAM: WTF unhandled case.");
        // console.log(pretty(newActiveModule));
      }
    }
  }

  render() {
    const { isMobileLandscape } = this.state;
    const { showGallery, activeModule, popover } = this.props;

    if (!showGallery) {
      return null;
    }

    return (
      <Portal node={popover}>
        <PopoverWrapper>
          <Show when={!activeModule.isFloorplan() && !activeModule.isAdvancedFloorplan()}>
            <GalleryInner isMobileLandscape={isMobileLandscape} galleryModule={activeModule} />
          </Show>

          <Show when={activeModule.isFloorplan()}>
            <FloorplansGallery isMobileLandscape={isMobileLandscape} galleryModule={activeModule} />
          </Show>

          <Show when={activeModule.isAdvancedFloorplan()}>
            <AdvancedFloorplans isMobileLandscape={isMobileLandscape} />
          </Show>
        </PopoverWrapper>
      </Portal>
    );
  }
}

/**
 * Map state to props.
 */
function mapStateToProps(state: GlobalState) {
  return {
    activeModule: getActiveModule(state),
    showGallery: shouldShowGallery(state),
    popover: window.famPopoverRef,
  };
}

export default connect(mapStateToProps)(FullscreenGalleryWrapper);
